import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { createEditorValue } from '@gitbook/doc-core';
import { Document, type DocumentJSON, deserialize, Value } from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';

import { BasicEditor } from './BasicEditor';

/**
 * Display a document from the API in a non-editable format: an agent's chat message, the summary
 * of a finding or of an insights topic.
 *
 * Normalized with the `display` schema rather than the `basic` one the composer writes with. Both
 * strip what they do not accept, and a stripped block takes its text with it, so the wider set is
 * what keeps a model's headings, code blocks and tables from arriving blank.
 */
export function BasicDocument(props: {
    document: Value | Document | api.JSONDocument;
    className?: string;
    typographyClassName?: string;
    plugins?: Plugin[];
}) {
    const { document, className, typographyClassName, plugins } = props;

    const value = React.useMemo(() => {
        if (Value.isValue(document)) {
            return document;
        }

        if (Document.isDocument(document)) {
            return createEditorValue(document, 'display');
        }

        return createEditorValue(deserialize(document as DocumentJSON), 'display');
    }, [document]);

    return (
        <BasicEditor
            value={value}
            readOnly
            // Sized by its content. Filling a flex column instead, documents stacked in one share its
            // height evenly: a short one padded with blank space, a long one scrolling inside.
            scrollable={false}
            onChange={() => {}}
            className={className}
            typographyClassName={typographyClassName}
            plugins={plugins}
        />
    );
}
